<template>
  <transition
    :enterActiveClass="$style.slideEnterActive"
    :enterClass="$style.slideEnter"
    :leaveActiveClass="$style.slideLeaveActive"
    :leaveToClass="$style.slideLeaveTo"
  >
    <div
      v-if="display"
      :class="$style.useCaseOnboarding"
    >
      <HeaderApp
        :class="$style.appbar"
        logoLink=""
      />

      <main :class="$style.mainContainer">
        <HeadingHuge
          :class="$style.heading"
          :data-test-id="$testId('heading')"
        >
          Welcome{{ firstName }}! Where would you like to start?
        </HeadingHuge>

        <HeadingSmall weight="normal">
          {{ useCaseHeading }}
        </HeadingSmall>

        <UseCaseGrid
          :class="$style.useCases"
          :selected="selectedUseCase"
          :useCases="useCases"
          @useCaseSelected="handleUseCaseSelected"
        />

        <transition
          :enterActiveClass="$style.fadeEnterActive"
          :enterClass="$style.fadeEnter"
          :leaveActiveClass="$style.fadeLeaveActive"
          :leaveToClass="$style.fadeLeaveTo"
          mode="out-in"
          name="fade"
        >
          <div
            v-if="isFeaturesVisible"
            key="UseCaseOnboarding-FeatureList"
          >
            <HeadingSmall
              :hasMargin="false"
              weight="normal"
            >
              {{ featureListHeading }}
            </HeadingSmall>

            <SelectChipGroup
              v-model="selectedFeatures"
              :options="availableFeatures"
              @input="updateResourceTypeFilter"
            />
          </div>

          <ParagraphBody
            v-else
            key="UseCaseOnboarding-FeatureList-EmptyState"
            weight="bold"
          >
            Begin with our
            <ButtonLink
              text="Feature List"
              @click="handleShowFeatures"
            />
            instead
          </ParagraphBody>
        </transition>

        <footer :class="$style.footer">
          <ButtonGroup :isFullWidth="false">
            <ButtonLink
              :data-test-id="$testId('skipButton')"
              text="Skip"
              @click="handleSkip"
            />

            <ProductTourLaunchButton
              :data-test-id="$testId('productTourButton')"
              :isDisabled="!continueEnabled"
              :location="$options.productTourLocation"
              type="outline"
              @click="handleLaunchProductTour"
            />

            <Button
              :data-test-id="$testId('submitButton')"
              :isDisabled="!continueEnabled"
              text="Let's Go!"
              @click="handleContinue"
            />
          </ButtonGroup>
        </footer>
      </main>
    </div>
  </transition>
</template>

<script>
import {
  Button,
  ButtonGroup,
  ButtonLink,
  HeaderApp,
  HeadingHuge,
  HeadingSmall,
  ParagraphBody,
  SelectChipGroup,
} from '@jumpcloud/ui-components';
import {
  chipGroupFeatures,
  defaultOnboardingFeatures,
  eventLocations,
  productTourLabels,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import { find, get } from 'lodash';
import { implementationChecklists } from 'client/components/ImplementationChecklist/ImplementationChecklists';
import { mapActions, mapGetters, mapState } from 'vuex';
import { shuffleArray } from 'client/GrowthEngineering/utilities/GrowthUtil';
import ChecklistMixin from 'client/components/ImplementationChecklist/mixins/ChecklistMixin';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import ProductTourLaunchButton from 'client/components/ProductTour/ProductTourLaunchButton';
import Util from 'client/util/Util';

import { marketingUseCases } from './UseCaseOnboardingConstants';
import UseCaseGrid from './UseCaseGrid.vue';

const onboardingModule = 'OnboardingModule';
const { createAdminAccount } = defaultOnboardingFeatures;
const defaultChecklistFeature = implementationChecklists[createAdminAccount];

export default {
  name: 'UseCaseOnboarding',

  components: {
    Button,
    ButtonLink,
    ButtonGroup,
    HeaderApp,
    HeadingHuge,
    HeadingSmall,
    ParagraphBody,
    ProductTourLaunchButton,
    SelectChipGroup,
    UseCaseGrid,
  },

  EventHelper,
  Util,
  productTourLocation: productTourLabels.onboardingLocation,

  mixins: [ChecklistMixin],

  data() {
    return {
      display: true,
      featureList: [],
      features: shuffleArray(chipGroupFeatures),
      isFeaturesVisible: true,
      selectedFeatures: [],
      selectedUseCase: '',
      useCases: {},
    };
  },

  computed: {
    ...mapState('SettingsModule', ['orgId']),

    ...mapGetters('SettingsModule', {
      user: 'getUser',
    }),

    ...mapGetters('OrganizationsCollection', ['getItemDetails']),

    availableFeatures() {
      return this.filterFeatures(this.features);
    },

    continueEnabled() {
      return this.selectedFeatures.length > 0;
    },

    featureListHeading() {
      return this.selectedUseCase
        ? "Great! We'll get you started with these related features:"
        : "Great! Select as many features as you'd like: ";
    },

    firstName() {
      if (this.user?.firstname) {
        return `, ${this.user.firstname}`;
      }

      return '';
    },

    marketingUseCase() {
      return get(this.getItemDetails(this.orgId), 'useCase', '');
    },

    useCaseHeading() {
      return 'You told us your primary interest. We will get you set up with the following related features.';
    },
  },

  created() {
    this.useCases = marketingUseCases;
    this.selectedUseCase = this.marketingUseCase;
    // need to search for the use-case object because we only get the label from data
    this.selectedFeatures = this.getFeaturesFromMarketing(this.selectedUseCase);
  },

  methods: {
    ...mapActions(onboardingModule, [
      'setInitialChecklistFeatures',
      'setShowChecklistLoading',
      'setShowProductTour',
      'updateOnboardingFlowComplete',
      'updateResourcesDisplayPreferences',
    ]),

    getFeaturesFromMarketing(selectedUseCase) {
      const useCase = find(marketingUseCases, { label: selectedUseCase });

      if (useCase) {
        return useCase.features;
      }

      return [];
    },

    handleContinue() {
      try {
        const selectedOptionsTextValues = this.selectedFeatures.map(option => option.name);
        const featureSelections = selectedOptionsTextValues.sort();

        this.$options.EventHelper.triggerButtonClicked({
          description: "User clicks the 'Let's go' button",
          features: this.selectedFeatures,
          location: eventLocations.useCaseOnboarding,
          multiSelectSubmitted: true,
          text: 'submit',
          useCase: this.selectedUseCase,
        });

        this.featureList = featureSelections;
        this.initializeChecklists();
        this.handleExit();
      } catch (error) {
        this.$options.EventHelper.triggerPageViewed({
          location: eventLocations.useCaseOnboarding,
          error,
        });
        this.$options.Util.redirect('/#/home');
      }
    },

    handleExit(redirectUrl = '/#/discover') {
      this.updateOnboardingFlowComplete();

      // If someone didn't select features or is on a small screen, move on without the animation
      if (this.featureList.length && !this.$options.Util.isSmallScreen()) {
        // add default feature to feature list
        const defaultFeature = defaultChecklistFeature.feature;
        this.featureList.unshift(defaultFeature);
        this.setInitialChecklistFeatures(this.featureList);
        this.setShowChecklistLoading(true);
      }

      this.$options.Util.redirect(redirectUrl);
      this.display = false;
    },

    handleLaunchProductTour() {
      this.$options.EventHelper.triggerButtonClicked({
        description: "User clicks the 'Launch Product Tour' button after selecting a use-case",
        features: this.selectedFeatures,
        location: eventLocations.useCaseOnboarding,
        text: 'launchProductTour',
        useCase: this.selectedUseCase,
      });

      this.initializeChecklists();
      this.setShowProductTour(true);
      this.handleExit();
    },

    handleShowFeatures() {
      this.selectedFeatures = [];
      this.isFeaturesVisible = true;
    },

    handleSkip() {
      this.$options.EventHelper.triggerButtonClicked({
        description: "User clicks the the 'Skip' button",
        features: this.selectedFeatures,
        location: eventLocations.useCaseOnboarding,
        text: 'skip',
        useCase: this.selectedUseCase,
      });

      this.handleExit();
    },

    handleUseCaseSelected(isUseCaseSelected, useCase) {
      if (isUseCaseSelected) {
        this.updateFeatures(useCase);
        this.isFeaturesVisible = true;
      } else {
        this.selectedUseCase = '';
        this.selectedFeatures = [];
        this.isFeaturesVisible = false;
      }
    },

    initializeChecklists() {
      this.initializeImplementationChecklists(this.selectedFeatures);
    },

    updateFeatures(useCase) {
      this.selectedUseCase = useCase.label;
      this.selectedFeatures = useCase.features;
    },

    updateResourceTypeFilter(features) {
      if (features.length === 1) {
        const resourcesDisplayPreferences = {
          resourceTypeFilter: features[0].value,
        };

        this.updateResourcesDisplayPreferences({
          resourcesDisplayPreferences,
        });
      }
    },
  },
};
</script>

<style module scoped>
.useCaseOnboarding {
  background-color: var(--jc-color-background);
  display: grid;
  grid-template-columns: auto;
  grid-template-rows: auto 1fr;
  height: 100vh;
  left: 0;
  position: fixed;
  top: 0;
  width: 100vw;
  z-index: 1000;
}

.appbar {
  background: var(--jc-color-surface-light);
}

.mainContainer {
  display: grid;
  grid-template-areas:
    "heading"
    "firstSubtext"
    "useCases"
    "secondSubtext"
    "chips"
    "footer";
  grid-template-columns: 100%;
  grid-template-rows: auto auto auto auto auto 1fr;
  height: 100%;
  overflow: auto;
  padding: var(--jc-size-4p5) var(--jc-size-4p5) var(--jc-size-2p5);
  width: 100%;
}

.heading.heading {
  grid-area: heading;
  margin: 0 0 var(--jc-size-3);
}

.useCases {
  margin-bottom: var(--jc-spacer-large);
}

.footer {
  align-self: end;
  grid-area: footer;
  margin-top: var(--jc-spacer-large);
}

.fadeEnterActive,
.fadeLeaveActive {
  transition: opacity 0.25s;
}

.fadeEnter,
.fadeLeaveTo {
  opacity: 0;
}

.slideEnterActive,
.slideLeaveActive {
  transition: opacity 0.5s ease;
}

.slideEnter,
.slideLeaveTo {
  opacity: 0;
  transform: translateY(-1em);
}
</style>
